import { useAtomValue } from 'jotai';
import { Fragment, type ReactNode,useState } from 'react';

import { Divider, Group, Stack, Tabs, Text } from '@ekhodealer/ekho-common/rev';

import type { TabItem} from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { EmptySection } from '@common/components/EmptySection';
import { LineItem } from '@common/components/LineItem';
import { StatBox } from '@common/components/StatBox';
import { formatCurrency } from '@common/lib/currency';
import { toSentenceCase } from '@common/lib/strings';

import { useGetOrderPaymentFai } from '../../api/useGetOrderPaymentFai';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';
import { SectionSkeleton } from '../order-details/shared/SectionSkeleton';

import { TransactionList } from './TransactionList';

const MY_PAYOUTS_TAB = 'my-payouts';
const SUMMARY_TAB = 'summary';
const MY_TRANSFERS_TAB = 'my-transfers';
const SECTION_TITLE = 'Payouts';

type SimplePayoutItem = {
	label: string;
	amount: number;
	service: string;
};

type TransferItem = {
	label: string;
	amount: number;
	date: string;
	status: string;
	source: string;
};

export const PayoutsSection = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const { data: orderPaymentFai, isLoading } = useGetOrderPaymentFai(
		selectedRow?.purchasedVehicleId || undefined,
	);
	const [selectedTab, setSelectedTab] = useState<string>(SUMMARY_TAB);
	if (isLoading) {
		return <SectionSkeleton title={SECTION_TITLE} />;
	}

	if (!orderPaymentFai) {
		return null;
	}

	const { payouts, buyerPayments } = orderPaymentFai;
	const {
		paid,
		balance,
		totalEkhoDeductions,
		ekhoDeductionPayouts,
		otherDeductionPayouts,
		totalOtherDeductions,
		netCreditsDeducted,
	} = payouts;
	const { grandTotal } = buyerPayments.receipt || { grandTotal: 0 };

	const allPayouts = (payouts as typeof payouts & { allPayouts?: SimplePayoutItem[] }).allPayouts;
	const myPayouts = (payouts as typeof payouts & { myPayouts?: SimplePayoutItem[] }).myPayouts;
	const myTransfers = (payouts as typeof payouts & { myTransfers?: TransferItem[] }).myTransfers;
	const myLenderPayouts = (payouts as typeof payouts & { myLenderPayouts?: SimplePayoutItem[] })
		.myLenderPayouts;

	const tabItems: TabItem[] = [
		{ id: SUMMARY_TAB, content: 'Summary' },
		{ id: MY_PAYOUTS_TAB, content: 'My payouts' },
		{ id: MY_TRANSFERS_TAB, content: 'My transfers' },
	];

	const handleTabSelect = (item: TabItem) => {
		setSelectedTab(item.id);
	};

	const renderSummaryContent = (
		payouts: SimplePayoutItem[] | undefined,
		totalLabel: string,
		emptyMessage: string,
	) => {
		if (!payouts || payouts.length === 0) {
			return <EmptySection message={emptyMessage} />;
		}
		const myLenderPayoutsTotal =
			myLenderPayouts?.reduce((sum, payout) => sum + payout.amount, 0) || 0;

		const ekhoDeductionSubItems = ekhoDeductionPayouts.map((item, index) => (
			<LineItem key={index} title={toSentenceCase(item.service)} price={item.amount} />
		));

		const summarySections: ReactNode[] = [
			<LineItem
				key='buyer-payment'
				title='Total expected buyer payment'
				price={grandTotal}
			/>,
		];

		if (netCreditsDeducted > 0) {
			summarySections.push(
				<LineItem
					key='credits'
					title='Credits deducted'
					price={
						<Text size='bodySmall' weight='semibold'>
							{netCreditsDeducted} credits
						</Text>
					}
				/>,
			);
		}

		summarySections.push(
			<LineItem
				key='ekho-deductions'
				title='Ekho deductions'
				price={totalEkhoDeductions}
				isToggleable
				subItems={ekhoDeductionSubItems}
			/>,
		);

		if (totalOtherDeductions > 0) {
			const otherDeductionSubItems = otherDeductionPayouts.map((item, index) => (
				<LineItem key={index} title={toSentenceCase(item.service)} price={item.amount} />
			));
			summarySections.push(
				<LineItem
					key='other-deductions'
					title='Other deductions'
					price={totalOtherDeductions}
					isToggleable
					subItems={otherDeductionSubItems}
				/>,
			);
		}

		if (myLenderPayouts && myLenderPayouts.length > 0) {
			const lenderSubItems = myLenderPayouts.map((item, index) => (
				<LineItem key={index} title={toSentenceCase(item.service)} price={item.amount} />
			));
			summarySections.push(
				<LineItem
					key='lender-incentives'
					title='Lender incentives'
					price={myLenderPayoutsTotal}
					isToggleable
					subItems={lenderSubItems}
				/>,
			);
		}

		summarySections.push(
			<LineItem
				key='net-total'
				title={totalLabel}
				price={
					grandTotal - totalEkhoDeductions - totalOtherDeductions + myLenderPayoutsTotal
				}
				isTotal
			/>,
		);

		return (
			<Stack itemsSpacing='0'>
				{summarySections.map((item, index) => (
					<Fragment key={index}>
						{index > 0 && <Divider />}
						{item}
					</Fragment>
				))}
			</Stack>
		);
	};

	const renderSimplePayoutsContent = (
		payouts: SimplePayoutItem[] | undefined,
		totalLabel: string,
		emptyMessage: string,
	) => {
		if (!payouts || payouts.length === 0) {
			return <EmptySection message={emptyMessage} />;
		}

		const total = payouts.reduce((sum, payout) => sum + payout.amount, 0);

		return (
			<Stack itemsSpacing='0'>
				{payouts.map((payout, index) => (
					<Fragment key={index}>
						<LineItem
							key={index}
							title={toSentenceCase(payout.service)}
							price={payout.amount}
						/>
						<Divider />
					</Fragment>
				))}
				<LineItem title={totalLabel} price={total} isTotal />
			</Stack>
		);
	};

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<Stack itemsSpacing='16'>
				<Group itemsSpacing='16' noWrap itemsAlignX='stretch'>
					<StatBox title='Paid' detail={formatCurrency(paid)} />
					<StatBox title='Balance' detail={formatCurrency(balance)} />
				</Group>

				<Tabs items={tabItems} selectedKey={selectedTab} onSelect={handleTabSelect} />
				{selectedTab === SUMMARY_TAB &&
					renderSummaryContent(
						allPayouts,
						'Net total expected',
						'Summary will appear here',
					)}
				{selectedTab === MY_PAYOUTS_TAB &&
					renderSimplePayoutsContent(
						myPayouts,
						'My total payouts',
						'Your payouts will appear here',
					)}
				{selectedTab === MY_TRANSFERS_TAB && (
					<TransactionList
						transactions={myTransfers}
						totalLabel='Total transfers'
						emptyMessage='Transfers will appear here'
					/>
				)}
			</Stack>
		</AccordionSection>
	);
};
